import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CircleChevronLeft, CircleChevronRight, RefreshCw } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'src/componentes/dropdown';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';

function Pagination({
  count,
  totalCount,
  currentPage,
  pageSize,
  allowPaging = true,
  changeCurrentPage,
  changePageSize,
  showPaginacaoSimples,
  pageSizeOptions,
  noLimitPageSize,
  disablePageSizeChange,
  filtroId,
  onRefreshClick,
  ...other
}) {
  const [paginationPageSize, setPaginationPageSize] = useState(pageSize);

  useEffect(() => {
    setPaginationPageSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    if (!filtroId) return;

    let active = true;

    const carregarPageSize = async () => {
      try {
        const response = await http.post(
          `/ListagemTelaColaborador/ObterPageSize`,
          {
            listId: filtroId,
          }
        );

        if (active && response.data) {
          setPaginationPageSize(response.data);
        }
      } catch (error) {
        if (active) errorHandler(error);
      }
    };

    carregarPageSize();

    return () => {
      active = false;
    };
  }, [filtroId]);

  const movePage = (diff) => {
    if (diff === 0 && onRefreshClick) {
      onRefreshClick();
    } else {
      changeCurrentPage(currentPage + diff);
    }
  };

  const beforeChangePageSize = (size) => {
    const pageCount = Math.max(Math.ceil(count / size), 1);
    if (currentPage > pageCount) changeCurrentPage(pageCount);

    changePageSize(size);
  };

  const pageCount = Math.max(Math.ceil(count / paginationPageSize), 1);
  const pages = Array.from(
    { length: pageCount },
    (_, index) => pageCount - index
  );
  const sizes =
    pageSizeOptions ?? (noLimitPageSize ? [160, 80, 40, 20, 10] : [40, 20, 10]);

  return (
    <div style={{ display: 'table' }} {...other}>
      {!showPaginacaoSimples && (
        <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
          <span className="mr-2">{totalCount ?? count}</span>
          <span className="mr-2">
            <FormattedMessage id="label.itens" />
          </span>
          <RefreshCw
            size={16}
            style={{ cursor: 'pointer' }}
            onClick={() => movePage(0)}
            color="#666"
          />
        </div>
      )}
      {allowPaging && paginationPageSize != 999999 && (
        <div
          style={{ display: 'table-cell', verticalAlign: 'middle' }}
          className="pl-3"
        >
          <span className="mr-2">
            <FormattedMessage id="label.pagina" />:
          </span>
          <CircleChevronLeft
            size={16}
            style={{ cursor: 'pointer' }}
            onClick={currentPage > 1 ? () => movePage(-1) : undefined}
            color="#666"
          />
        </div>
      )}
      {allowPaging && paginationPageSize != 999999 && (
        <div
          style={{ display: 'table-cell', verticalAlign: 'middle' }}
          className="pl-2"
        >
          <UncontrolledDropdown>
            <DropdownToggle
              caret
              size="sm"
              className="tw:gap-1 tw:has-[>svg]:px-2"
            >
              {currentPage}
            </DropdownToggle>
            <DropdownMenu>
              {pages &&
                pages.length > 0 &&
                pages.map((p) => (
                  <DropdownItem key={p} onClick={() => changeCurrentPage(p)}>
                    {p}
                  </DropdownItem>
                ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      )}
      {allowPaging && paginationPageSize != 999999 && (
        <div
          style={{ display: 'table-cell', verticalAlign: 'middle' }}
          className="pl-2"
        >
          <span className="mr-2">
            <span className="mr-2">
              <FormattedMessage id="label.de" />
            </span>
            <span className="mr-2">{pageCount}</span>
            <CircleChevronRight
              size={16}
              style={{ cursor: 'pointer' }}
              onClick={currentPage < pageCount ? () => movePage(1) : undefined}
              color="#666"
            />
          </span>
        </div>
      )}
      {allowPaging && !showPaginacaoSimples && paginationPageSize != 999999 && (
        <div
          style={{ display: 'table-cell', verticalAlign: 'middle' }}
          className="pl-2"
        >
          <UncontrolledDropdown>
            <DropdownToggle
              caret
              size="sm"
              className="tw:gap-1 tw:has-[>svg]:px-2"
              disabled={disablePageSizeChange}
            >
              {paginationPageSize}
            </DropdownToggle>
            <DropdownMenu>
              {sizes.map((size) => (
                <DropdownItem
                  key={size}
                  onClick={() => beforeChangePageSize(size)}
                >
                  {size}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      )}
      {allowPaging && !showPaginacaoSimples && (
        <div
          style={{ display: 'table-cell', verticalAlign: 'middle' }}
          className="pl-2"
        >
          <span>
            <FormattedMessage id="label.itensPorPagina" />
          </span>
        </div>
      )}
    </div>
  );
}

export default Pagination;

Pagination.propTypes = {
  count: PropTypes.number,
  totalCount: PropTypes.number,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  allowPaging: PropTypes.bool,
  compact: PropTypes.bool,
  index: PropTypes.number,
};
